import React from 'react';
import useStoryblokGlobalNav from './useStoryblokGlobalNav';
import { NanoNavItem, NanoCta, NanoMdd, NanoMddItem } from '@nanoporetech-digital/components-react';
import { useStoryblokHref } from './helpers';
import * as styles from './style.module.css';

// Recursively renders the Storyblok global-nav `menu_primary` tree into the v9
// design-system nav format. Mirrors the shared Vue GlobalNav implementation so
// the markup matches the current design system. All markup is produced at build
// time — the tree is sourced from Storyblok during `sourceNodes`.
export default function MegaDropDown() {
    const { menu_primary: menuPrimary = [] } = useStoryblokGlobalNav();

    return (
        <>
            {menuPrimary.map((rootItem, i) => (
                <RootItem key={rootItem._uid ?? i} item={rootItem} />
            ))}
        </>
    );
}

// A top-level nav entry: a direct link if it carries a Storyblok link, otherwise
// a nav item that opens a secondary panel containing its child blocks.
function RootItem({ item }) {
    const resolveHref = useStoryblokHref();
    const label = item.title || item.label;
    // A root `link` can be either a plain Storyblok link object (legacy direct
    // link) or an array of `simple-link` bloks (a "View all …" link). The latter
    // belongs to the menu as a whole, so it renders beneath the first column.
    const linkBloks = Array.isArray(item.link) ? item.link : null;
    const href = linkBloks ? null : resolveHref(item.link);

    if (href) {
        // This PWA *is* the store, so the incoming "Store" entry always
        // represents the current site and is marked active.
        const isActive = label === 'Store';
        return (
            <a slot="menu" href={href} className={isActive ? 'menu-item active' : 'menu-item'}>
                {label}
            </a>
        );
    }

    const children = item.items ?? [];
    const hasViewAll = Array.isArray(linkBloks) && linkBloks.length > 0;
    const hasSecondary = children.length > 0 || hasViewAll;

    return (
        <NanoNavItem slot="menu" closeOnBlur>
            {label}
            {hasSecondary && <button slot="close-button">{label}</button>}
            {hasSecondary && (
                <NanoMdd slot="secondary">
                    {children.map((child, i) => (
                        <MddColumn
                            key={child._uid ?? i}
                            item={child}
                            extraViewAll={i === 0 ? linkBloks : null}
                        />
                    ))}
                </NanoMdd>
            )}
        </NanoNavItem>
    );
}

// A second-level block becomes one column (`nano-mdd-item`) in the mega-dropdown:
// a titled list of links, or a call-to-action. A menu's own `link` simple-links
// render as "View all …" links beneath its list; `extraViewAll` carries any
// menu-level "View all" that belongs beneath the first column.
function MddColumn({ item, extraViewAll }) {
    if (item.component === 'menu-cta') {
        return (
            <NanoMddItem>
                <MenuCta item={item} />
            </NanoMddItem>
        );
    }

    const links = item.items ?? [];
    const ownViewAll = Array.isArray(item.link) ? item.link : [];
    const viewAll = [...ownViewAll, ...(extraViewAll ?? [])];

    return (
        <NanoMddItem>
            {item.title && <span slot="label">{item.title}</span>}
            {item.intro && <p className={styles.intro}>{item.intro}</p>}
            {links.length > 0 && (
                <ul className="mdd__link-columns">
                    {links.map((child, i) => {
                        if (child.component === 'menu-link') {
                            return (
                                <li key={child._uid ?? i}>
                                    <MenuLink item={child} />
                                </li>
                            );
                        }

                        if (child.component === 'menu') {
                            const subItems = child.items ?? [];
                            return (
                                <li key={child._uid ?? i}>
                                    <ul className={styles.subMenu}>
                                        <li>{child.title}</li>
                                        {subItems.map((sub, si) => (
                                            <li key={sub._uid ?? si}>
                                                <MenuLink item={sub} />
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            );
                        }

                        return null;
                    })}
                </ul>
            )}
            {viewAll.length > 0 && <ViewAllLinks bloks={viewAll} />}
        </NanoMddItem>
    );
}

function MenuCta({ item }) {
    const resolveHref = useStoryblokHref();
    const href = resolveHref(item.link);

    return (
        <>
            {item.body && <p dangerouslySetInnerHTML={{ __html: item.body }} />}
            <NanoCta>
                <a href={href ?? '#'}>{item.link_label}</a>
            </NanoCta>
        </>
    );
}

// Renders Storyblok `simple-link` bloks as "View all …" links at the bottom of a
// section. Mirrors SimpleLink.vue: an anchor when the link resolves, otherwise a
// non-interactive span so we never emit an empty/broken anchor.
function ViewAllLinks({ bloks }) {
    return (bloks ?? []).map((blok, i) => <ViewAllLink key={blok._uid ?? i} blok={blok} />);
}

function ViewAllLink({ blok }) {
    const resolveHref = useStoryblokHref();

    if (!blok.link_label) return null;

    const href = resolveHref(blok.link);

    if (href) {
        return (
            <a className="mdd__view-all" href={href} target={blok.link?.target || undefined}>
                {blok.link_label}
            </a>
        );
    }

    return <span className="mdd__view-all">{blok.link_label}</span>;
}

function MenuLink({ item }) {
    const resolveHref = useStoryblokHref();
    const href = resolveHref(item.link);

    return (
        <a href={href ?? '#'} target={item.link?.target || undefined}>
            {item.label}
            {item.sub_text && (
                <span className={styles.subText}>{item.sub_text}</span>
            )}
        </a>
    );
}
